import React, { useState, useEffect } from 'react';
import { X, ImagePlus } from 'lucide-react'; // Ensure you have lucide-react installed or replace with another icon library

interface CustomImageUploadProps {
    onFileChange: (file: File | null) => void;
    currentFile: File | null;
}

const CustomImageUpload: React.FC<CustomImageUploadProps> = ({ onFileChange, currentFile }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // If there's a currentFile, set the preview from it
        if (currentFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
            };
            reader.readAsDataURL(currentFile);
        }
    }, [currentFile]);

    const handleFile = (file: File | null) => {
        if (file) {
            setIsLoading(true);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
                setIsLoading(false);
            };
            reader.readAsDataURL(file);
            onFileChange(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        handleFile(file);
    };

    return (
        <div
            className={`rounded-xl border-2 border-dashed ${
                isDragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
            } transition-all relative group`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="p-6 text-center">
                {preview ? (
                    <div className="relative">
                        <img src={preview} alt="Preview" className="rounded-lg w-full h-32 object-cover" />
                        <button
                            type="button"
                            onClick={() => {
                                setPreview(null);
                                onFileChange(null);
                            }}
                            className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm border hover:bg-gray-50"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-4">
                            <ImagePlus size={32} className={`mx-auto ${isLoading ? 'animate-pulse' : 'text-gray-400'}`} />
                        </div>
                        <input
                            type="file"
                            id="image-upload"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => handleFile(e.target.files ? e.target.files[0] : null)}
                        />
                        <label
                            htmlFor="image-upload"
                            className="cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                            {isLoading ? 'Uploading...' : 'Click to upload'}
                        </label>
                        <p className="mt-1 text-xs text-gray-500">or drag and drop</p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default CustomImageUpload;
